/**
 * AddButtonLinkActionDialog - Dialog para adicionar/editar ação de botão com link
 * 
 * Envia mensagem com botão CTA (Call-to-Action) clicável via Z-API send-button-actions.
 * Diferente do send_link, o botão é sempre clicável independente do contato estar salvo.
 * 
 * @version 1.0.0
 * @date 2026-02-19
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { MousePointerClick } from 'lucide-react';

// =====================================================
// INTERFACES
// =====================================================

/** Estrutura do action_value para send_button_link */
interface ButtonLinkActionData {
  message: string;
  button_label: string;
  link_url: string;
  title?: string;
  footer?: string;
}

interface AddButtonLinkActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: ButtonLinkActionData) => void;
  initialValue?: Record<string, unknown>;
}

// =====================================================
// COMPONENTE
// =====================================================

export function AddButtonLinkActionDialog({
  open,
  onOpenChange,
  onSave,
  initialValue,
}: AddButtonLinkActionDialogProps) {
  const [message, setMessage] = useState('');
  const [buttonLabel, setButtonLabel] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [title, setTitle] = useState('');
  const [footer, setFooter] = useState('');

  // Reset ao abrir dialog
  useEffect(() => {
    if (open) {
      if (initialValue) {
        setMessage((initialValue.message as string) || '');
        setButtonLabel((initialValue.button_label as string) || '');
        setLinkUrl((initialValue.link_url as string) || '');
        setTitle((initialValue.title as string) || '');
        setFooter((initialValue.footer as string) || '');
      } else {
        setMessage('');
        setButtonLabel('Acessar');
        setLinkUrl('');
        setTitle('');
        setFooter('');
      }
    }
  }, [open, initialValue]);

  // Handler de salvar
  const handleSave = () => {
    if (message.trim() && linkUrl.trim() && buttonLabel.trim()) {
      onSave({
        message: message.trim(),
        button_label: buttonLabel.trim(),
        link_url: linkUrl.trim(),
        title: title.trim() || undefined,
        footer: footer.trim() || undefined,
      });
    }
  };

  // Validação: mensagem, URL e label do botão são obrigatórios
  const isValid = message.trim().length > 0 
    && linkUrl.trim().startsWith('http') 
    && buttonLabel.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MousePointerClick className="h-5 w-5" />
            {initialValue ? 'Editar Botão com Link' : 'Adicionar Botão com Link'}
          </DialogTitle>
          <DialogDescription>
            Envia uma mensagem com um botão CTA clicável. O destinatário pode clicar no botão
            para abrir o link diretamente, sem precisar ter o número salvo.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Título opcional (header) */}
          <div className="space-y-2">
            <Label htmlFor="btn-title">Título (opcional)</Label>
            <Input
              id="btn-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Título da mensagem"
              maxLength={60}
            />
          </div>

          {/* Mensagem principal */}
          <div className="space-y-2">
            <Label htmlFor="btn-message">Mensagem *</Label>
            <Textarea
              id="btn-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Olá {{contact_name}}, clique no botão abaixo para acessar:"
              rows={3}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              Variáveis: <code className="bg-muted px-1 rounded">{'{{contact_name}}'}</code>, <code className="bg-muted px-1 rounded">{'{{contact_first_name}}'}</code>, <code className="bg-muted px-1 rounded">{'{{contact_phone}}'}</code>
            </p>
          </div>

          {/* Rodapé opcional */}
          <div className="space-y-2">
            <Label htmlFor="btn-footer">Rodapé (opcional)</Label>
            <Input
              id="btn-footer"
              value={footer}
              onChange={(e) => setFooter(e.target.value)}
              placeholder="Ex: Válido por 7 dias"
              maxLength={60}
            />
          </div>

          {/* URL do botão */}
          <div className="space-y-2">
            <Label htmlFor="btn-link-url">URL do Botão *</Label>
            <Input
              id="btn-link-url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://exemplo.com/proposta"
            />
          </div>

          {/* Texto do botão */}
          <div className="space-y-2">
            <Label htmlFor="btn-label">Texto do Botão *</Label>
            <Input
              id="btn-label"
              value={buttonLabel}
              onChange={(e) => setButtonLabel(e.target.value)}
              placeholder="Acessar Proposta"
              maxLength={25}
            />
            <p className="text-xs text-muted-foreground">
              Máximo 25 caracteres. Esse texto aparece no botão clicável.
            </p>
          </div>

          {/* Preview visual simulando WhatsApp */}
          {message && (
            <div className="p-3 border rounded-lg bg-muted/50 space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Preview</p>
              <div className="bg-card border rounded-lg p-3 max-w-[280px] space-y-2">
                {title && (
                  <p className="text-sm font-bold">{title}</p>
                )}
                <p className="text-sm whitespace-pre-wrap">{message}</p>
                {footer && (
                  <p className="text-xs text-muted-foreground">{footer}</p>
                )}
                <div className="border-t pt-2 mt-2">
                  <div className="flex items-center justify-center gap-2 text-primary text-sm font-medium py-1.5">
                    <MousePointerClick className="h-4 w-4" />
                    {buttonLabel || 'Acessar'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={!isValid}>
            {initialValue ? 'Salvar' : 'Adicionar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
